import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Send, Undo2, Pencil, Trash2, CalendarClock, RotateCcw, Instagram, Facebook, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, formatDateTime } from "@/lib/api";
import { StatusBadge, LANG_SHORT, errMsg, toLocalIST } from "./mk";
import { StatusShare } from "./StatusShare";
import { mediaUrl } from "./mkMedia";

const StatusBlock = ({ p, i, onMake }) => {
  if (!["approved", "scheduled", "posting", "posted"].includes(p.status)) return null;
  const sv = p.status_version || {};
  if (sv.state === "ready") return <StatusShare item={{ path: sv.path, kind: sv.kind, title: p.title, caption: p.wa_caption || p.caption }} testPrefix={`mk-status-${i}`} />;
  if (sv.state === "making" && (!sv.at || Date.now() - new Date(sv.at).getTime() < 25 * 60000)) return <p className="text-xs text-slate-500 flex items-center gap-1" data-testid={`mk-status-making-${i}`}><Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing WhatsApp Status version…</p>;
  return <Button size="sm" variant="outline" className="h-8 text-green-700" onClick={onMake} data-testid={`mk-status-make-${i}`}>Make WhatsApp Status version</Button>;
};

const Act = ({ icon: Icon, label, onClick, testId, variant = "outline", cls = "" }) => (
  <Button size="sm" variant={variant} onClick={onClick} data-testid={testId} className={`h-8 ${cls}`}><Icon className="mr-1 h-3.5 w-3.5" />{label}</Button>
);

export default function PostCard({ post: p, i, onChange, onEdit, liveOff }) {
  const [busy, setBusy] = useState(false);
  const [when, setWhen] = useState(toLocalIST(p.scheduled_at));
  const [showSched, setShowSched] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const making = p.status_version?.state === "making";
  useEffect(() => {
    if (!making) return undefined;
    const t = setInterval(async () => {
      const { data } = await api.get(`/admin/marketing/posts/${p.id}`).catch(() => ({ data: null }));
      if (data && data.status_version?.state !== "making") onChange(data);
    }, 4000);
    return () => clearInterval(t);
  }, [making, p.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const call = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); if (ok) toast.success(ok); onChange(data); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
    finally { setBusy(false); }
  };
  const act = (path, ok, body) => call(() => api.post(`/admin/marketing/posts/${p.id}/${path}`, body || {}), ok);
  const del = () => { if (window.confirm("Delete this draft?")) call(() => api.delete(`/admin/marketing/posts/${p.id}`).then(() => ({ data: { id: p.id, _deleted: true } })), "Deleted"); };
  const watch = async () => {
    for (let k = 0; k < 40; k++) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await api.get(`/admin/marketing/posts/${p.id}`).catch(() => ({ data: null }));
      if (data && data.status !== "posting") { onChange(data); toast[data.status === "posted" ? "success" : "error"](data.status === "posted" ? "Posted" : "Posting failed"); return; }
    }
  };
  const postNow = async () => { if (window.confirm("Post this now to the selected platforms?") && await act("post-now", "Posting…")) watch(); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row gap-4" data-testid={`mk-post-${i}`}>
      <div className="relative h-28 w-28 shrink-0 rounded-xl bg-slate-100 overflow-hidden">
        {p.media_type === "reel" && p.reel?.cover_path ? <img src={mediaUrl(p.reel.cover_path)} alt="" className="h-full w-full object-cover" />
          : p.image_path && <img src={resolveImg(p.image_path)} alt="" className="h-full w-full object-cover" />}
        {p.media_type === "reel" && <span className="absolute bottom-1 left-1 rounded bg-pink-600 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`mk-post-reel-${i}`}>REEL{p.reel?.has_music ? " ♪" : ""}</span>}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={p.status} test={p.test_mode} testId={`mk-post-status-${i}`} />
          {p.status === "scheduled" && liveOff && <span className="text-[11px] font-bold text-amber-700" data-testid={`mk-post-waiting-${i}`}>waiting for live posting</span>}
          <span className="text-[11px] font-bold rounded bg-blue-50 text-blue-800 px-1.5 py-0.5">{LANG_SHORT[p.language]}</span>
          {p.platforms.includes("instagram") && <Instagram className="h-4 w-4 text-pink-600" />}
          {p.platforms.includes("facebook") && <Facebook className="h-4 w-4 text-blue-700" />}
          {busy && <Loader2 className="h-4 w-4 animate-spin text-blue-700" />}
        </div>
        <div className="font-bold text-slate-900 truncate">{p.title}</div>
        <p className="text-sm text-slate-600 line-clamp-2">{p.caption}</p>
        <div className="text-xs text-slate-500 space-x-3">
          {p.scheduled_at && <span>{p.status === "draft" ? "Planned" : "Scheduled"}: {formatDateTime(p.scheduled_at)} IST</span>}
          {p.posted_at && <span>Posted: {formatDateTime(p.posted_at)}</span>}
          {p.approved_by && <span>Approved by {p.approved_by}</span>}
        </div>
        {p.error && <p className="text-xs text-red-600 font-medium" data-testid={`mk-post-error-${i}`}>{p.error}</p>}
        <StatusBlock p={p} i={i} onMake={() => call(() => api.post(`/admin/marketing/posts/${p.id}/status-version`).then(() => ({ data: { ...p, status_version: { state: "making" } } })), "Preparing WhatsApp Status version…")} />
        {showSched && (
          <div className="flex flex-wrap gap-2 items-center">
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-8 w-56" data-testid={`mk-sched-input-${i}`} />
            <Button size="sm" className="h-8 bg-indigo-700" onClick={async () => { if (await act("schedule", "Scheduled", { scheduled_local: when })) setShowSched(false); }} data-testid={`mk-sched-save-${i}`}>Save time (IST)</Button>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {p.status === "draft" && <><Act icon={Check} label="Approve" variant="default" cls="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("approve", "Approved")} testId={`mk-approve-${i}`} />
            <Act icon={Pencil} label="Edit" onClick={() => onEdit(p)} testId={`mk-edit-${i}`} />
            <Act icon={Trash2} label="Delete" cls="text-red-600" onClick={del} testId={`mk-delete-${i}`} /></>}
          {["approved", "scheduled"].includes(p.status) && <><Act icon={CalendarClock} label={p.status === "scheduled" ? "Change time" : "Schedule"} onClick={() => setShowSched((s) => !s)} testId={`mk-schedule-${i}`} />
            <Act icon={Send} label="Post now" variant="default" cls="bg-blue-800" onClick={postNow} testId={`mk-postnow-${i}`} /></>}
          {p.status === "failed" && <><Act icon={RotateCcw} label="Retry" variant="default" cls="bg-blue-800" onClick={postNow} testId={`mk-retry-${i}`} />
            <Act icon={Pencil} label="Edit" onClick={() => onEdit(p)} testId={`mk-edit-${i}`} /></>}
          {["approved", "scheduled", "failed"].includes(p.status) && <Act icon={Undo2} label="Back to draft" variant="ghost" onClick={() => act("unapprove", "Moved back to draft")} testId={`mk-unapprove-${i}`} />}
          {p.status === "posted" && p.test_mode && <Act icon={Undo2} label="Move back to Approved" onClick={() => window.confirm("This post was only a test and never reached Facebook/Instagram. Move it back to Approved so you can schedule it for real?") && act("reset-test", "Moved back to Approved")} testId={`mk-reset-test-${i}`} />}
          <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500" onClick={() => setShowHist((s) => !s)} data-testid={`mk-history-${i}`}>History</Button>
        </div>
        {showHist && (
          <ul className="text-xs text-slate-500 border-t border-slate-100 pt-2 space-y-0.5">
            {(p.history || []).slice().reverse().map((h, k) => <li key={k}>{formatDateTime(h.at)} — <b className="text-slate-700">{h.action}</b> by {h.by}{h.note ? ` (${h.note})` : ""}</li>)}
            {Object.entries(p.results || {}).map(([pf, r]) => r.id && <li key={pf}>{pf}: {r.id}{r.test_mode ? " (test mode — not really posted)" : ""}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}
